import { type FC } from "react";

import { type DocumentFragment, type RunMetaFragment } from "@app_schema";

import { FilePill } from "@styled/file_pill";
import { RunMeta } from "@styled/run_meta";
import { Stack } from "@styled/stack";

import { filePillStatus } from "./shared_card_utils";

// The detail query selects `run` on each source document inline (not via the
// shared document fragment), so widen the row shape to expose it.
type SourceDoc = DocumentFragment & { run?: RunMetaFragment | null };

/**
 * Vertical stack of `FilePill`s for the source documents attached to a record
 * review. Used inside `ProcessRecordsCard` to surface the actual PDFs the user
 * uploaded during upload/processing/failure states.
 *
 * Renders nothing when no files are attached so callers can drop it in
 * unconditionally.
 */
export const SourceFileList: FC<{
  files: SourceDoc[];
  projectID: string;
}> = ({ files, projectID }) => {
  if (files.length === 0) return null;
  return (
    <Stack>
      {files.map((doc) => (
        <FilePill
          key={doc.id}
          name={doc.name}
          type={doc.file.type}
          size={doc.file.bytesize}
          status={filePillStatus(doc)}
          to={`/dashboard/projects/${projectID}/documents/${doc.id}`}
          meta={doc.run ? <RunMeta run={doc.run} /> : undefined}
        />
      ))}
    </Stack>
  );
};

// Borderline domain logic: encodes "1 file → name, N files → count". Watch
// for duplication elsewhere; if it shows up a second time, push to the server
// as a resolved `sourceFilesSummary` field on `RecordReview`.
export function describeFiles(files: DocumentFragment[]): string | undefined {
  if (files.length === 0) return undefined;
  if (files.length === 1) return files[0]!.name;
  return `${files.length} files`;
}
